/**
 * Package Hooks - React hooks for package data management
 * Package System — product/service bundles
 */

import {
  addPackageItem,
  createPackage,
  deletePackage,
  forkPackage,
  getLinkedPackagesByProduct,
  getPackageById,
  getPackages,
  getPackagesByEquipmentCategory,
  getPackagesByType,
  getPackagesForEquipment,
  removePackageItem,
  updatePackage,
  updatePackageItem,
  type AddPackageItemDTO,
  type CreatePackageDTO,
  type ForkPackageDTO,
  type Package,
  type PackageCopyResult,
  type PackageFilters,
  type PackageListResponse,
  type PackagesByTypeResponse,
  type PackageType,
  type UpdatePackageDTO,
  type UpdatePackageItemDTO,
} from '@/api/packages';
import { useCallback, useEffect, useState } from 'react';

// ============================================
// TYPES
// ============================================

interface UsePackagesResult {
  packages: Package[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

interface UsePackageResult {
  pkg: Package | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

interface UsePackagesByTypeResult {
  packages: Package[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

interface UseMutationResult<TArgs extends unknown[]> {
  mutate: (...args: TArgs) => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

// ============================================
// HOOKS
// ============================================

/**
 * Hook to fetch and manage package list
 */
export function usePackages(filters: PackageFilters = {}): UsePackagesResult {
  const [packages, setPackages] = useState<Package[]>([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, limit: 20, hasMore: false });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const filtersKey = JSON.stringify(filters);
  const fetchPackages = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response: PackageListResponse = await getPackages(filters);
      setPackages(response.data);
      setMeta(response.meta);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch packages';
      setError(message);
      console.error('Error fetching packages:', err);
    } finally {
      setIsLoading(false);
    }
  }, [filtersKey]);

  useEffect(() => {
    void fetchPackages();
  }, [fetchPackages]);

  return {
    packages,
    total: meta.total,
    page: meta.page,
    limit: meta.limit,
    hasMore: meta.hasMore,
    isLoading,
    error,
    refetch: fetchPackages,
  };
}

/**
 * Hook to fetch single package by ID
 */
export function usePackage(id: string | undefined): UsePackageResult {
  const [pkg, setPkg] = useState<Package | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPackage = useCallback(async () => {
    if (!id) {
      setPkg(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const response = await getPackageById(id);
      setPkg(response.data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch package';
      setError(message);
      console.error('Error fetching package:', err);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void fetchPackage();
  }, [fetchPackage]);

  return {
    pkg,
    isLoading,
    error,
    refetch: fetchPackage,
  };
}

/**
 * Hook to fetch packages by type
 */
export function usePackagesByType(type: PackageType | undefined): UsePackagesByTypeResult {
  const [packages, setPackages] = useState<Package[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchByType = useCallback(async () => {
    if (!type) {
      setPackages([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const response: PackagesByTypeResponse = await getPackagesByType(type);
      setPackages(response.data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch packages by type';
      setError(message);
      console.error('Error fetching packages by type:', err);
    } finally {
      setIsLoading(false);
    }
  }, [type]);

  useEffect(() => {
    void fetchByType();
  }, [fetchByType]);

  return {
    packages,
    isLoading,
    error,
    refetch: fetchByType,
  };
}

/**
 * Hook to fetch active RENTAL packages linked to a specific equipment category.
 * Only fetches when category is defined.
 * Used in rental wizard EquipmentStep.
 */
export function usePackagesByEquipmentCategory(
  category: string | undefined
): UsePackagesByTypeResult {
  const [packages, setPackages] = useState<Package[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchByCategory = useCallback(async () => {
    if (!category) {
      setPackages([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const response = await getPackagesByEquipmentCategory(category);
      setPackages(response.data);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to fetch packages by equipment category';
      setError(message);
      console.error('Error fetching packages by equipment category:', err);
    } finally {
      setIsLoading(false);
    }
  }, [category]);

  useEffect(() => {
    void fetchByCategory();
  }, [fetchByCategory]);

  return {
    packages,
    isLoading,
    error,
    refetch: fetchByCategory,
  };
}

/**
 * Hook to create a package — returns created package so caller can access the new ID
 */
export function useCreatePackage(): {
  mutate: (data: CreatePackageDTO) => Promise<Package | undefined>;
  isLoading: boolean;
  error: string | null;
} {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutate = useCallback(async (data: CreatePackageDTO): Promise<Package | undefined> => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await createPackage(data);
      return response.data;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create package';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { mutate, isLoading, error };
}

/**
 * Hook to update a package
 */
export function useUpdatePackage(): UseMutationResult<[string, UpdatePackageDTO]> {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutate = useCallback(async (id: string, data: UpdatePackageDTO) => {
    setIsLoading(true);
    setError(null);
    try {
      await updatePackage(id, data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update package';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { mutate, isLoading, error };
}

/**
 * Hook to delete a package
 */
export function useDeletePackage(): UseMutationResult<[string]> {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutate = useCallback(async (id: string) => {
    setIsLoading(true);
    setError(null);
    try {
      await deletePackage(id);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete package';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { mutate, isLoading, error };
}

/**
 * Hook to add an item to a package — returns updated package
 */
export function useAddPackageItem(): {
  mutate: (packageId: string, data: AddPackageItemDTO) => Promise<Package | undefined>;
  isLoading: boolean;
  error: string | null;
} {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutate = useCallback(
    async (packageId: string, data: AddPackageItemDTO): Promise<Package | undefined> => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await addPackageItem(packageId, data);
        return response.data;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to add package item';
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  return { mutate, isLoading, error };
}

/**
 * Hook to remove an item from a package
 */
export function useRemovePackageItem(): UseMutationResult<[string, string]> {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutate = useCallback(async (packageId: string, itemId: string) => {
    setIsLoading(true);
    setError(null);
    try {
      await removePackageItem(packageId, itemId);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to remove package item';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { mutate, isLoading, error };
}

/**
 * Hook to update an existing package item attribute (PATCH) — returns updated package.
 * P1-fix: a PackageEditorPage meglévő-item mező-szerkesztés perzisztálásához.
 */
export function useUpdatePackageItem(): {
  mutate: (
    packageId: string,
    itemId: string,
    data: UpdatePackageItemDTO
  ) => Promise<Package | undefined>;
  isLoading: boolean;
  error: string | null;
} {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutate = useCallback(
    async (
      packageId: string,
      itemId: string,
      data: UpdatePackageItemDTO
    ): Promise<Package | undefined> => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await updatePackageItem(packageId, itemId, data);
        return response.data;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to update package item';
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  return { mutate, isLoading, error };
}

/**
 * STORY-1206 #14 stage-2 — copy-on-write fork hook.
 * Visszaadja a PackageCopyResult-ot, hogy a hívó a variant-ra navigálhasson.
 */
export function useForkPackage(): {
  mutate: (id: string, data: ForkPackageDTO) => Promise<PackageCopyResult>;
  isLoading: boolean;
  error: string | null;
} {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutate = useCallback(
    async (id: string, data: ForkPackageDTO): Promise<PackageCopyResult> => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await forkPackage(id, data);
        return response.data;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to fork package';
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  return { mutate, isLoading, error };
}

/**
 * Hook to fetch packages linked to a specific rental equipment (unified lookup).
 * Merges CATEGORY-linked + EQUIPMENT-linked packages from the backend.
 * Only fetches when equipmentId is defined.
 */
export function usePackagesForEquipment(equipmentId: string | undefined): UsePackagesByTypeResult {
  const [packages, setPackages] = useState<Package[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchForEquipment = useCallback(async () => {
    if (!equipmentId) {
      setPackages([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const response = await getPackagesForEquipment(equipmentId);
      setPackages(response.data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch packages for equipment';
      setError(message);
      console.error('Error fetching packages for equipment:', err);
    } finally {
      setIsLoading(false);
    }
  }, [equipmentId]);

  useEffect(() => {
    void fetchForEquipment();
  }, [fetchForEquipment]);

  return {
    packages,
    isLoading,
    error,
    refetch: fetchForEquipment,
  };
}

/**
 * Hook to fetch packages that contain a specific product (reverse lookup).
 * Used in product detail views to show which packages include this product.
 * Only fetches when productId is defined.
 */
export function useLinkedPackagesByProduct(productId: string | undefined): UsePackagesByTypeResult {
  const [packages, setPackages] = useState<Package[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchLinkedByProduct = useCallback(async () => {
    if (!productId) {
      setPackages([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const response = await getLinkedPackagesByProduct(productId);
      setPackages(response.data);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to fetch linked packages by product';
      setError(message);
      console.error('Error fetching linked packages by product:', err);
    } finally {
      setIsLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    void fetchLinkedByProduct();
  }, [fetchLinkedByProduct]);

  return {
    packages,
    isLoading,
    error,
    refetch: fetchLinkedByProduct,
  };
}
